import React, { useCallback, useEffect, useMemo, useState, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMe } from '@/lib/api';

const AuthContext = createContext({ user: null, loading: true, refresh: () => {}, setUser: () => {} });

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        // CRITICAL: Skip /me check if returning from OAuth callback (AuthCallback handles it)
        if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }
        try {
            const u = await getMe();
            setUser(u);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // Memoise context value so consumers don't re-render when only the
    // parent re-renders without user/loading changing.
    const value = useMemo(
        () => ({ user, loading, refresh, setUser }),
        [user, loading, refresh],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const RequireAdmin = ({ children }) => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        if (!loading && (!user || user.role !== 'admin')) {
            navigate('/admin/login', { replace: true });
        }
    }, [user, loading, navigate]);
    if (loading || !user || user.role !== 'admin') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-la-canvas text-la-text2">
                Verifying access…
            </div>
        );
    }
    return children;
};
